import { useEffect } from 'react';

/**
 * The app shell's height on phones (index.css `.app-viewport-h`): `100vh`, then `100dvh` where supported. In Chrome
 * the on-screen keyboard resizes the layout viewport (index.html sets `interactive-widget=resizes-content`), so
 * `100dvh` already shrinks with it. Browsers that shrink only the VISUAL viewport for the keyboard (iOS Safari,
 * Chrome before 108, some WebViews) leave dvh at full height, which would put the composer behind the keyboard.
 * There this hook publishes the visual viewport's height as `--app-vv-height` (with `data-vv-height` on <html>),
 * and the shell uses it instead.
 *
 * Never while pinch-zoomed (the visual viewport is smaller then, but the app must not shrink), and only when the
 * visual viewport is clearly shorter than the layout viewport (keyboard), so ordinary address-bar movement keeps the
 * dvh path. Removed as soon as the condition ends.
 */
export function useAppViewportHeight(enabled: boolean): void {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    const clear = () => {
      root.removeAttribute('data-vv-height');
      root.removeAttribute('data-vv-short');
      root.style.removeProperty('--app-vv-height');
    };
    if (!enabled || !vv) { clear(); return; }
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const zoomed = Math.abs(vv.scale - 1) > 0.01;
        const shrunk = window.innerHeight - vv.height > 80;
        if (!zoomed && shrunk) {
          root.style.setProperty('--app-vv-height', `${Math.round(vv.height)}px`);
          root.setAttribute('data-vv-height', '');
          // A very short visible area (a landscape phone with the keyboard open): see `.hide-when-short`.
          root.toggleAttribute('data-vv-short', vv.height < 300);
        } else {
          clear();
        }
      });
    };
    update();
    vv.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      clear();
    };
  }, [enabled]);
}
